import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight, CheckCircle2, MessageCircle, Phone } from "lucide-react";

import { mock, saveLead, loadLeads } from "@/mock";
import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/sonner";

import { Reveal } from "@/components/site/SiteLayout";

const schema = z.object({
  name: z.string().min(2, "Please enter your name"),
  email: z.string().email("Please enter a valid email"),
  phone: z.string().min(6, "Please enter a phone number"),
  service: z.string().min(1, "Please select a service"),
  message: z.string().min(10, "Tell us a bit more (min 10 chars)"),
});

function GlassPanel({ className, children }) {
  return (
    <div
      className={cn(
        "rounded-3xl border border-white/10 bg-white/[0.06] backdrop-blur-[18px]",
        "shadow-[inset_0_1px_0_rgba(255,255,255,0.14),_0_18px_60px_rgba(0,0,0,0.55)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

export default function Contact() {
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      service: "",
      message: "",
    },
  });

  const [recent, setRecent] = useState(() => loadLeads().slice(0, 4));

  const serviceOptions = useMemo(() => mock.services.map((s) => s.title), []);

  const onSubmit = async (values) => {
    // Frontend-only: save to localStorage as a teaser.
    const next = saveLead(values);
    setRecent(next.slice(0, 4));

    toast.success("Request received", {
      description: "Saved locally for now (mock). Backend lead capture can be added next.",
    });

    form.reset();
  };

  return (
    <div className="mx-auto max-w-[1200px] px-5 py-16">
      <div className="grid gap-10 lg:grid-cols-[1fr_0.95fr]">
        <div>
          <Reveal delay={0}>
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-black/40 px-4 py-2 text-xs text-white/70">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              Contact
            </div>
          </Reveal>
          <Reveal delay={90}>
            <h1 className="mt-5 text-3xl font-semibold tracking-tight text-white md:text-4xl">
              Tell us what you’re building
            </h1>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-white/70">
              Share your goals and constraints. We’ll respond with next steps, estimated timelines, and a proposal.
            </p>
          </Reveal>

          <Reveal delay={160}>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {[{
                title: "Email",
                value: mock.company.contact.email,
                href: `mailto:${mock.company.contact.email}`,
              }, {
                title: "WhatsApp",
                value: mock.company.contact.whatsapp,
                href: `https://wa.me/${mock.company.contact.whatsapp.replace(/\D/g, "")}`,
              }, {
                title: "Location",
                value: mock.company.contact.location,
              }, {
                title: "Response time",
                value: "Typically within 24–48 hours",
              }].map((i) => (
                <GlassPanel key={i.title} className="p-5">
                  <div className="text-xs tracking-[0.18em] text-white/55">
                    {i.title}
                  </div>
                  <div className="mt-2 text-sm text-white/80">
                    {i.href ? (
                      <a
                        href={i.href}
                        target={i.href.startsWith("http") ? "_blank" : undefined}
                        rel={i.href.startsWith("http") ? "noreferrer" : undefined}
                        className="transition-[color] duration-200 hover:text-white"
                      >
                        {i.value}
                      </a>
                    ) : (
                      i.value
                    )}
                  </div>
                </GlassPanel>
              ))}
            </div>
          </Reveal>

          <Separator className="my-10 bg-white/10" />

          <Reveal delay={240}>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button
                asChild
                className="h-[52px] rounded-xl bg-primary text-black btn-glow hover:bg-primary/90"
              >
                <a
                  href={`https://wa.me/${mock.company.contact.whatsapp.replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <MessageCircle className="mr-2 h-4 w-4" />
                  WhatsApp quick connect
                </a>
              </Button>
              <Button
                variant="secondary"
                asChild
                className="h-[52px] rounded-xl border border-white/15 bg-black/50 text-white hover:bg-primary/10"
              >
                <a href={`mailto:${mock.company.contact.email}`}>
                  <Phone className="mr-2 h-4 w-4" />
                  Email us
                </a>
              </Button>
            </div>
          </Reveal>
        </div>

        <div id="form">
          <Reveal delay={120}>
            <GlassPanel className="p-7">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xs tracking-[0.22em] text-white/55">
                    GET A QUOTE
                  </div>
                  <div className="mt-2 text-xl font-semibold tracking-tight text-white">
                    Project brief
                  </div>
                  <div className="mt-2 text-sm text-white/70">
                    This form currently saves to your browser only (**mock**). We can wire it to the backend next.
                  </div>
                </div>
                <div className="grid h-10 w-10 place-items-center rounded-2xl border border-white/10 bg-black/55">
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                </div>
              </div>

              <div className="mt-6">
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="grid gap-4"
                  >
                    <div className="grid gap-4 md:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white/80">Name</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Your name"
                                className="rounded-xl border-white/15 bg-black/40 text-white placeholder:text-white/40 focus-visible:ring-primary/45"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white/80">Email</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="you@company.com"
                                className="rounded-xl border-white/15 bg-black/40 text-white placeholder:text-white/40 focus-visible:ring-primary/45"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white/80">Phone</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Phone / WhatsApp"
                                className="rounded-xl border-white/15 bg-black/40 text-white placeholder:text-white/40 focus-visible:ring-primary/45"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="service"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-white/80">Service</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger className="rounded-xl border-white/15 bg-black/40 text-white focus:ring-primary/45">
                                  <SelectValue placeholder="Choose" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent className="bg-black border-white/10 text-white">
                                {serviceOptions.map((s) => (
                                  <SelectItem key={s} value={s}>
                                    {s}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white/80">Message</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="What are you building? What’s the timeline? Any constraints?"
                                className="min-h-[120px] rounded-xl border-white/15 bg-black/40 text-white placeholder:text-white/40 focus-visible:ring-primary/45"
                              {...field}
                            />
                          </FormControl>
                          <FormDescription className="text-white/50">
                            Include links, references, and the success metric you care about.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      className={cn(
                        "h-[52px] w-full rounded-xl bg-primary text-black btn-glow",
                        "transition-[transform,box-shadow,background-color] duration-200",
                        "hover:bg-primary/90 hover:-translate-y-[1px]",
                      )}
                    >
                      Send request
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </form>
                </Form>
              </div>
            </GlassPanel>
          </Reveal>

          <Reveal delay={260}>
            <div className="mt-6 rounded-3xl border border-white/10 bg-white/[0.05] p-6 backdrop-blur-[18px]">
              <div className="text-sm font-medium text-white">Recent requests (local)</div>
              <div className="mt-2 text-xs text-white/55">
                Visible only in your browser (for now).
              </div>

              <div className="mt-5 grid gap-3">
                {recent.length === 0 ? (
                  <div className="rounded-2xl border border-white/10 bg-black/40 px-4 py-4 text-sm text-white/60">
                    No submissions yet.
                  </div>
                ) : (
                  recent.map((r) => (
                    <Card key={r.id} className="rounded-2xl border-white/10 bg-black/40">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-white">
                          {r.name} • {r.service}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="text-xs text-white/55">{r.email} • {r.phone}</div>
                        <div className="mt-2 text-sm text-white/70 line-clamp-3">{r.message}</div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
